<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { get } from 'svelte/store';
  import {
    ArrowLeft,
    ArrowRight,
    AtSign,
    Building2,
    CalendarDays,
    ClipboardList,
    Compass,
    FileSpreadsheet,
    MapPin,
    Radar,
    Search,
    Scale,
    SlidersHorizontal,
    Sparkles,
    UserRound
  } from 'lucide-svelte';

  import StepIndustry from './StepIndustry.svelte';
  import StepLegalForm from './StepLegalForm.svelte';
  import StepTechnology from './StepTechnology.svelte';
  import StepSamples from './StepSamples.svelte';
  import IndustryInfo from '$lib/components/IndustryInfo.svelte';
  import ScreenView from '$lib/components/ScreenView.svelte';
  import StepChannels from './StepChannels.svelte';
  import StepFounding from './StepFounding.svelte';
  import StepRefine from './StepRefine.svelte';
  import StepRegion from './StepRegion.svelte';
  import StepRole from './StepRole.svelte';
  import StepExclude from './StepExclude.svelte';
  import StepListUpload from './StepListUpload.svelte';
  import StepListProfile from './StepListProfile.svelte';

  import { goto } from '$app/navigation';

  import { env } from '$lib/config/environment';
  import { showFrontDoor } from '$lib/stores/navigate';
  import { aiModeActive } from '$lib/stores/ai';
  import { applicationMode } from '$lib/stores/mode';
  import { submitFilters } from '$lib/api/filters';
  import { submitNeighborSamples } from '$lib/api/neighbors';
  import { neighborSamples } from '$lib/stores/neighbors';
  import { selectedIndustryCentroids } from '$lib/stores/data';
  import { industryProfile } from '$lib/stores/industryProfile';
  import { applyExclusion, customerList, listExcluded } from '$lib/api/lists';
  import {
    advance,
    countState,
    guidedActive,
    guidedDoor,
    guidedStep,
    retreat,
    scheduleCount,
    stepsFor,
    mandatorySteps,
    type Door
  } from '$lib/stores/guided';
  import { billableRecords } from '$lib/pricing/pricing';
  import { resultCap } from '$lib/stores/guided';
  import {
    cantons,
    centerPostalCode,
    companySizeRange,
    contactMode,
    contactRoles,
    contactTitles,
    excludeLegalForm,
    foundingYearRange,
    hasEmail,
    hasFacebook,
    hasInstagram,
    hasLinkedin,
    hasPhone,
    hasTwitter,
    hasWebsite,
    languages,
    legalForm,
    technologies,
    mailGaps,
    postalCodes,
    queryFilters,
    radiusKm
  } from '$lib/stores/selection';
  import { sidebarOpen } from '$lib/stores/ui';
  import type { Filters } from '$lib/types/api';

  /**
   * Classic mode's front door: a guided search with a running total.
   *
   * # What it replaces
   *
   * An empty state with two cards that were not buttons, the same sentence
   * printed three times, and a "search all companies" button that ran an
   * unfiltered query. Nothing on it was a step and nothing on it said what
   * anything would return.
   *
   * # It writes into the same stores as the filter panel
   *
   * Every choice here lands in `queryFilters` and `selectedIndustryCentroids`.
   * Leaving at any point — the skip link, or finishing — drops the customer
   * into the ordinary classic screen with exactly what they chose already
   * applied. There is no draft to commit and nothing to translate.
   */

  /**
   * The filters as they were when the current step opened.
   *
   * Kept so that the moment the running total reaches zero the screen can say
   * which step did it and offer to put it back. That moment is the old
   * screen's worst failure — filter, get nothing, no idea which choice was
   * responsible — and it is the one thing a step-by-step flow can fix for
   * free, because it knows what changed and when.
   */
  let snapshot: { filters: Filters; centroids: Map<string, string> } | null = $state(null);

  const steps = $derived($guidedDoor ? stepsFor($guidedDoor) : []);
  const position = $derived(steps.indexOf($guidedStep));
  const isLast = $derived(position === steps.length - 1);

  const count = $derived($countState.value);

  /**
   * The selection has just been narrowed to nothing.
   *
   * Measured on companies, not contacts: with no contact role chosen the
   * contact count is legitimately zero while the selection is enormous, and
   * reading that as "you have excluded everything" would fire the warning on
   * the industry step every time.
   */
  const emptied = $derived(
    $countState.status === 'ready' &&
      count !== null &&
      count.companies === 0 &&
      ($countState.lastNonEmpty ?? 0) > 0
  );

  /**
   * Whether the step on screen is missing the one thing it cannot proceed
   * without.
   *
   * Only the examples qualify. Every other step is either the door's own
   * question, which a customer may legitimately leave broad, or an optional
   * filter — but similarity with nothing to resemble is refused by the search
   * itself, and finding that out at the end of the sequence is finding it out
   * five questions too late.
   */
  const blocked = $derived($guidedStep === 'samples' && $neighborSamples.length === 0);

  /**
   * A count is in flight, so the figures on screen describe the step before
   * this one.
   *
   * Every step re-asks how large the selection is, and the answer decides two
   * things the customer is about to act on: what the summary bar says the
   * search will cost, and whether the last step is allowed to run at all. Left
   * unlocked, "Weiter" moves on numbers belonging to the previous answer —
   * pick a canton, click through, and the bar spends the next moment
   * disagreeing with the step you are now looking at.
   *
   * It covers the whole sequence rather than the last step alone: the guide's
   * point is that the total is visible while it is being built, and a total
   * that is one question behind is worse than one that takes a moment.
   *
   * The similarity door never counts, so this is never true there.
   */
  const counting = $derived($countState.status === 'counting');

  /** What this selection would be charged for, and whether it is deliverable. */
  const billable = $derived(count ? billableRecords(count.companies, count.contacts) : 0);
  const tooLarge = $derived($countState.status === 'ready' && billable > resultCap);

  function takeSnapshot() {
    snapshot = {
      filters: structuredClone(get(queryFilters)),
      centroids: new Map(get(selectedIndustryCentroids))
    };
  }

  function choose(door: Door) {
    guidedDoor.set(door);
    guidedStep.set(stepsFor(door)[0]);
    takeSnapshot();
  }

  function next() {
    if (isLast) {
      run();
      return;
    }
    advance();
    takeSnapshot();
  }

  function back() {
    retreat();
    takeSnapshot();
  }

  function undo() {
    if (!snapshot) return;
    queryFilters.set(structuredClone(snapshot.filters));
    selectedIndustryCentroids.set(new Map(snapshot.centroids));
    scheduleCount();
  }

  /**
   * Finish: leave the guided screen and run the search it describes.
   *
   * Which search that is depends on the door. The similarity flow collected
   * examples rather than an industry, and `submitNeighborSamples` reads the
   * same stores this guide has been writing all along — the samples, the
   * filters, the centroids — so finishing is a matter of calling the other one
   * and moving the app into the mode whose screen can show what comes back.
   *
   * The mode goes into the URL as well as the store, because the app reads it
   * back from there on load: setting the store alone gives a result screen that
   * a reload turns into an empty classic search.
   */
  function run() {
    guidedActive.set(false);
    sidebarOpen.set(false);

    if ($guidedDoor === 'similarity') {
      applicationMode.set('neighbor');
      goto('/?mode=neighbor');
      submitNeighborSamples();
      return;
    }

    submitFilters();
  }

  /**
   * Hand the search over to the assistant instead.
   *
   * The door used to offer "straight to the filters", which is not an offer
   * worth making on the first screen: it asks somebody who has just arrived to
   * decide against a guide they have not seen yet, and the filters are what
   * they will get anyway once the guide has filled them in. The alternative
   * worth naming is the other way of describing a search — say it in words and
   * let the assistant map it — so that is what sits there now.
   *
   * The guide only ever runs inside classic mode, so turning the assistant on
   * is the whole switch; there is no mode to change underneath it.
   */
  /**
   * The three ways in, in the order they are offered.
   *
   * Two of them open a door of the guide and the third leaves it, so each
   * carries what to do rather than a door name — the cards are identical to
   * look at and a customer choosing between them is not being asked which kind
   * of thing they are choosing.
   */
  const ways = [
    { id: 'industry', icon: Building2, go: () => choose('industry') },
    { id: 'role', icon: UserRound, go: () => choose('role') },
    { id: 'similarity', icon: Radar, go: () => choose('similarity') },
    { id: 'list', icon: FileSpreadsheet, go: () => choose('list') }
  ];

  function startAdvisor() {
    guidedActive.set(false);
    aiModeActive.set(true);
  }

  /**
   * A mark per step.
   *
   * Every step is the same shape by construction — same width, same heading,
   * same bands — which is what makes the sequence feel like one thing and also
   * what makes one step hard to tell from the next when you glance up. The
   * mark is the difference.
   */
  /**
   * Which optional steps the customer has asked for.
   *
   * # Every step after the first is opt-in
   *
   * The door asks the one thing this search is about — an industry, or a role —
   * and that step is put directly. Everything after it is a filter the customer
   * may well not want, so it asks before it shows: a sequence that presents six
   * panels of controls reads as six things to fill in, which is the opposite of
   * guided.
   *
   * # Turning one off clears it
   *
   * Otherwise a filter set and then unticked keeps narrowing the search from a
   * screen that no longer shows it — the same silent-leftover bug the region
   * type switch had.
   */
  let enabled = $state(new Set<string>());

  /**
   * Take or drop the category whose profile is open.
   *
   * The same write IndustryTree does on a row, and the same recount after it —
   * the running total at the foot of the guide has to answer a choice made from
   * this screen exactly as it answers one made from the list.
   */
  function toggleProfiledCentroid() {
    const centroid = get(industryProfile);
    if (!centroid) return;

    selectedIndustryCentroids.update((selected) => {
      const next = new Map(selected);
      const key = String(centroid.id);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, centroid.name);
      }
      return next;
    });
    scheduleCount();
  }

  const currentYear = new Date().getFullYear();

  /** Whether a step already carries a value, from a previous visit or the panel. */
  function hasValue(step: string): boolean {
    switch (step) {
      case 'samples':
        return $neighborSamples.length > 0;
      case 'industry':
        return $selectedIndustryCentroids.size > 0;
      case 'role':
        // Either way of naming a person counts as having answered the step.
        return $contactRoles.length > 0 || $contactTitles.length > 0;
      case 'region':
        return (
          $cantons.length > 0 ||
          $postalCodes.length > 0 ||
          $languages.length > 0 ||
          ($centerPostalCode !== '' && $radiusKm > 0)
        );
      case 'refine':
        return $companySizeRange.some((v) => v !== null);
      case 'technology':
        // Either list is an answer. They are two different questions — what a
        // company uses, and what its mail is missing — and the step is
        // answered by having said something about either.
        return $technologies.length > 0 || $mailGaps.length > 0;
      case 'legalForm':
        // Either column is an answer: "only AGs" and "no Einzelfirmen" are both
        // the customer having decided something about legal form.
        return $legalForm.length > 0 || $excludeLegalForm.length > 0;
      case 'channels':
        return (
          $hasWebsite ||
          $hasEmail ||
          $hasPhone ||
          $hasLinkedin ||
          $hasFacebook ||
          $hasInstagram ||
          $hasTwitter
        );
      case 'founding':
        return $foundingYearRange.some((v) => v !== null);
      case 'exclude':
        // A list that is held and switched on is an answer; one that is held
        // and switched off is the customer having said no to this step.
        return $customerList !== null && $listExcluded;
      default:
        return false;
    }
  }

  /**
   * The steps that make up the door's own question are not optional.
   *
   * One for three of the doors. Two for the list door, whose question takes a
   * file and then the findings read out of it — see mandatorySteps.
   */
  const optional = $derived(position >= ($guidedDoor ? mandatorySteps($guidedDoor) : 1));
  const showFilter = $derived(!optional || enabled.has($guidedStep) || hasValue($guidedStep));

  function clearStep(step: string) {
    switch (step) {
      case 'samples':
        neighborSamples.set([]);
        break;
      case 'industry':
        selectedIndustryCentroids.set(new Map());
        break;
      case 'role':
        contactRoles.set([]);
        contactTitles.set([]);
        contactMode.set('single');
        break;
      case 'region':
        cantons.set([]);
        postalCodes.set([]);
        languages.set([]);
        centerPostalCode.set('');
        radiusKm.set(0);
        break;
      case 'refine':
        companySizeRange.set([null, null]);
        break;
      case 'legalForm':
        legalForm.set([]);
        excludeLegalForm.set([]);
        break;
      case 'technology':
        technologies.set([]);
        mailGaps.set([]);
        break;
      case 'channels':
        [
          hasWebsite,
          hasEmail,
          hasPhone,
          hasLinkedin,
          hasFacebook,
          hasInstagram,
          hasTwitter
        ].forEach((flag) => flag.set(false));
        break;
      case 'founding':
        foundingYearRange.set([null, null]);
        break;
      case 'exclude':
        // Only the subtraction is undone. The list itself stays uploaded —
        // deleting somebody's customer base because they clicked "no" on a
        // wizard step would be the app taking a decision that is theirs.
        listExcluded.set(false);
        applyExclusion();
        break;
    }
  }

  /** Yes: show this filter, and give it something to show where it needs one. */
  function enableStep() {
    const step = $guidedStep;
    enabled = new Set(enabled).add(step);

    // The founding filter is a range or nothing, and its panel reads the store
    // to know which. Asking for it means asking for the widest range.
    if (step === 'founding') foundingYearRange.set([1800, currentYear]);

    scheduleCount();
  }

  /** No: forget whatever this step set, and go on to the next question. */
  function skipStep() {
    const step = $guidedStep;
    const next = new Set(enabled);
    next.delete(step);
    enabled = next;

    clearStep(step);
    scheduleCount();

    if (!isLast) {
      advance();
      takeSnapshot();
    }
  }

  const stepIcons: Record<string, typeof Compass> = {
    samples: Radar,
    industry: Building2,
    role: UserRound,
    region: MapPin,
    refine: SlidersHorizontal,
    legalForm: Scale,
    channels: AtSign,
    founding: CalendarDays,
    exclude: ClipboardList,
    listUpload: FileSpreadsheet,
    listProfile: Sparkles
  };
</script>

<!--
  A category's profile takes the screen rather than floating over it.

  It replaces the step it was opened from instead of rendering inside it: the
  step's own header says "Which industries?" over a progress bar, and a
  category profile underneath that heading is a screen claiming to be a
  different one. Back puts the step, and the customer's expanded groups,
  straight back.
-->
{#if $industryProfile}
  <IndustryInfo
    centroidId={$industryProfile.id}
    name={$industryProfile.name}
    selected={$selectedIndustryCentroids.has(String($industryProfile.id))}
    onToggle={() => toggleProfiledCentroid()}
    onClose={() => industryProfile.set(null)}
  />
{:else if $guidedStep === 'door'}
  <ScreenView icon={Compass} title={$_('guided.door.title')} subtitle={$_('guided.door.subtitle')}>
    <!--
      Two by two rather than four across: at four columns each card is narrow
      enough that its two lines of description wrap to four, and the row reads
      as a toolbar rather than as a choice between ways of working.
    -->
    <div class="grid gap-4 sm:grid-cols-2">
      {#each ways as option (option.id)}
        <button
          type="button"
          onclick={option.go}
          class="group rounded-surface border border-hairline p-6 text-left transition-colors hover:border-accent-400 hover:bg-surface-canvas"
        >
          <div
            class="mb-4 flex h-12 w-12 items-center justify-center rounded-surface bg-accent-50 text-accent-600"
          >
            <option.icon class="h-6 w-6" />
          </div>
          <h2 class="mb-1 text-base font-semibold text-gray-900">
            {$_(`guided.door.${option.id}Title`)}
          </h2>
          <p class="text-sm text-gray-600">{$_(`guided.door.${option.id}Body`)}</p>
          <span
            class="mt-4 inline-flex items-center gap-1 text-sm font-medium text-accent-600 group-hover:gap-2"
          >
            {$_('guided.start')}
            <ArrowRight class="h-4 w-4 transition-colors" />
          </span>
        </button>
      {/each}
    </div>

    <svelte:fragment slot="footer">
      <span></span>
      <button
        type="button"
        onclick={startAdvisor}
        class="inline-flex items-center gap-2 rounded-control px-3 py-2 text-sm font-medium text-accent-600 hover:bg-accent-50"
      >
        <Sparkles class="h-4 w-4" />
        {$_('guided.startAdvisor')}
      </button>
    </svelte:fragment>
  </ScreenView>
{:else}
  <!--
    The way out, distinct from the footer's "back".

    That one steps to the previous question; this one leaves the guide
    altogether. `showFrontDoor` is the same call the members' navigation makes
    for "Dashboard", and it deliberately does not reset the guide — a count
    somebody is part-way through collecting survives the trip.

    The label names where it goes, which is not the same place in both builds:
    a member lands on the dashboard, a visitor has none and lands back on the
    door the guide starts from.
  -->
  <ScreenView
    icon={stepIcons[$guidedStep]}
    title={$_(`guided.${$guidedStep}.title`)}
    subtitle={$_(`guided.${$guidedStep}.subtitle`)}
    step={position + 1}
    steps={steps.length}
    onBack={showFrontDoor}
    backLabel={env.mode === 'member' ? $_('guided.leaveToDashboard') : $_('guided.leaveToStart')}
  >
    <!--
      No card around the step.

      The panel's `p-5` pushed everything inside it twenty pixels right of the
      title above, so the heading and the first line of what it described did
      not share a left edge — which reads as carelessness even when nothing is
      actually misaligned. The controls in each step carry their own borders;
      wrapping them in another one was a box inside a box.
    -->
    {#if optional && !showFilter}
      <!--
        The question, and nothing else.

        A checkbox stood here, with the filter appearing beneath it once
        ticked — which made the step look like a form with one field answered
        and the rest still to come. Asking outright is what a guide does: the
        box holds the question until it is answered, and then holds the answer.
      -->
      <div class="flex h-full min-h-[16rem] flex-col items-center justify-center text-center">
        <p class="max-w-md text-lg font-medium text-gray-900">
          {$_(`guided.${$guidedStep}.ask`)}
        </p>

        <div class="mt-6 flex items-center gap-3">
          <button
            type="button"
            onclick={enableStep}
            class="rounded-control bg-accent-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent-700"
          >
            {$_('guided.ask.yes')}
          </button>

          <button
            type="button"
            onclick={skipStep}
            class="rounded-control border border-hairline px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:border-hairline-strong hover:text-gray-900"
          >
            {$_('guided.ask.no')}
          </button>
        </div>
      </div>
    {/if}

    {#if showFilter}
      {#if $guidedStep === 'samples'}
        <StepSamples />
      {:else if $guidedStep === 'industry'}
        <StepIndustry />
      {:else if $guidedStep === 'role'}
        <StepRole />
      {:else if $guidedStep === 'region'}
        <StepRegion />
      {:else if $guidedStep === 'legalForm'}
        <StepLegalForm />
      {:else if $guidedStep === 'technology'}
        <StepTechnology />
      {:else if $guidedStep === 'refine'}
        <StepRefine />
      {:else if $guidedStep === 'channels'}
        <StepChannels />
      {:else if $guidedStep === 'founding'}
        <StepFounding />
      {:else if $guidedStep === 'exclude'}
        <StepExclude />
      {:else if $guidedStep === 'listUpload'}
        <StepListUpload />
      {:else if $guidedStep === 'listProfile'}
        <StepListProfile />
      {/if}

      {#if optional}
        <!--
          The way back out. Without it, answering yes is irreversible from
          here: the founding filter in particular writes a range on being
          asked for, and clearing it by hand means knowing that a full-width
          range is not the same as no range at all.
        -->
        <button
          type="button"
          onclick={skipStep}
          class="mt-4 text-xs text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline"
        >
          {$_('guided.ask.undo')}
        </button>
      {/if}
    {/if}

    {#if emptied}
      <div class="mb-5 rounded-surface border border-amber-200 bg-amber-50 px-4 py-3">
        <p class="text-sm text-amber-900">
          {$_('guided.emptied.message', {
            values: { before: ($countState.lastNonEmpty ?? 0).toLocaleString('de-CH') }
          })}
        </p>
        <button
          type="button"
          onclick={undo}
          class="mt-2 text-sm font-medium text-amber-900 underline underline-offset-2"
        >
          {$_('guided.emptied.undo')}
        </button>
      </div>
    {/if}

    {#if tooLarge}
      <div class="rounded-surface border border-amber-200 bg-amber-50 px-4 py-3">
        <p class="text-sm text-amber-900">
          {$_('guided.tooLarge.message', {
            values: {
              count: billable.toLocaleString('de-CH'),
              cap: resultCap.toLocaleString('de-CH')
            }
          })}
        </p>
        <p class="mt-1 text-xs text-amber-800/80">{$_('guided.tooLarge.hint')}</p>
      </div>
    {/if}

    <!--
        No count bar here. The summary bar at the foot of the window shows the
        same figures from the same source while the guide is up — two running
        totals on one screen is one more than a customer can act on.
      -->

    <svelte:fragment slot="footer">
      <button
        type="button"
        onclick={back}
        class="inline-flex items-center gap-1.5 rounded-control px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
      >
        <ArrowLeft class="h-4 w-4" />
        {$_('guided.back')}
      </button>

      <div class="flex items-center gap-2">
        <button
          type="button"
          onclick={next}
          disabled={blocked || counting || (isLast && tooLarge)}
          class="inline-flex items-center gap-2 rounded-control bg-accent-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:hover:bg-gray-300"
        >
          {#if counting}
            <!--
              Said, not just done. A button that goes grey on click and comes
              back a moment later reads as a misfire; the spinner is what makes
              the wait legible as a wait.
            -->
            <span class="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
            ></span>
            {$_(isLast ? 'guided.showResults' : 'guided.next')}
          {:else if isLast}
            <Search class="h-4 w-4" />
            {$_('guided.showResults')}
          {:else}
            {$_('guided.next')}
            <ArrowRight class="h-4 w-4" />
          {/if}
        </button>
      </div>
    </svelte:fragment>
  </ScreenView>
{/if}
